import React from 'react';
import { Tooltip, type TooltipPlacement } from './ui/Tooltip';

export interface RescheduleEntry {
  reason: string;
  old_date: string;
  new_date: string;
  created: string;
}

interface ServiceFailureBadgeProps {
  /** Reschedule history to list in the hover tooltip. */
  history: RescheduleEntry[];
  /** Delivery reschedule count shown in the badge. */
  count: number;
  /** Dark-mode flag — tooltip is portaled, so colors are applied inline. */
  /** Tooltip placement; defaults to 'right'. */
  placement?: TooltipPlacement;
}

/**
 * Small count badge for an order's delivery reschedules ("Service Failures")
 * with a hover tooltip listing each reschedule (old → new date, reason, when).
 * The badge is orange for a single reschedule and red for two or more. Shared by
 * the At-Risk Monitor and the Dispatch / LC dashboards. Renders nothing when
 * count < 1.
 */
export const ServiceFailureBadge: React.FC<ServiceFailureBadgeProps> = ({
  history,
  count,
  placement = 'right',
}) => {
  if (!count || count < 1) {
    return null;
  }

  const tooltipContent = history.length > 0 ? (
    <div className="text-xs space-y-2 max-w-xs">
      <div className="font-semibold pb-1 mb-1 border-b border-gray-300 text-gray-900 dark:border-gray-600 dark:text-gray-50">
        Service Failure History ({count})
      </div>
      {history.map((entry, idx) => (
        <div key={idx} className="space-y-0.5">
          <div className="text-muted">{entry.old_date} → {entry.new_date}</div>
          <div className="font-medium text-gray-900 dark:text-gray-50">{entry.reason || 'No reason provided'}</div>
          <div className="text-[0.625rem]" style={{ color: 'rgb(var(--c-gray-400))' }}>{entry.created}</div>
        </div>
      ))}
    </div>
  ) : null;

  return (
    <Tooltip content={tooltipContent} placement={placement}>
      <span
        className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded cursor-help ${
          count === 1 ? 'bg-orange-500' : 'bg-red-500'
        }`}
      >
        {count}
      </span>
    </Tooltip>
  );
};
